import type { ShortTextQuestion } from '@shared/domain/form';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { motion } from 'framer-motion';
import React from 'react';

interface ReviewShortQuestionProps {
  question: ShortTextQuestion;
  formFields: Record<string, any>;
  handleFormFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  errors: Record<string, string>;
  isConditional?: boolean;
}

// Gentle settle, no scale pop — SCSS glow ring carries the attention.
const CONDITIONAL_ENTRANCE = {
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

const DEFAULT_ENTRANCE = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25, ease: 'easeOut' as const },
};

function ReviewShortTextQuestion(props: ReviewShortQuestionProps) {
  const { question, formFields, handleFormFieldChange, errors, isConditional = false } = props;
  const hasError = !!errors[question.name];
  const motionProps = isConditional ? CONDITIONAL_ENTRANCE : DEFAULT_ENTRANCE;
  const className = [
    'review-question',
    isConditional && 'review-question--conditional',
    hasError && 'review-question--has-error',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.section className={className} data-error-name={question.name} data-testid={question.name} {...motionProps}>
      <h3 id={`${question.name}-label`} className="review-question__prompt">
        {question.question}
      </h3>

      <div className="review-question__answer">
        <input
          type="text"
          name={question.name}
          aria-labelledby={`${question.name}-label`}
          aria-invalid={hasError}
          onChange={handleFormFieldChange}
          value={formFields[question.name] || ''}
          className={`review-input${hasError ? ' review-input--error' : ''}`}
        />

        {hasError && (
          <div className="review-error" role="alert">
            <WarningAmberIcon />
            <span>{errors[question.name]}</span>
          </div>
        )}
      </div>
    </motion.section>
  );
}

export default ReviewShortTextQuestion;
